/**
 * What: Versioned contract for merchant-supplied Shopify onboarding data.
 * Why: One questionnaire contains brief claims, declared channels, and
 * experience preferences with different scopes and allowed uses. This registry
 * preserves those boundaries before values reach persistence or prompts.
 */
import * as z from 'zod';
import { merchantBriefRegistry, merchantStoreGoalIdSchema, merchantStoreGoalIds, merchantStoreGoalLabels, } from './merchant-brief.js';
export const MERCHANT_ONBOARDING_QUESTIONNAIRE_VERSION = 2;
export const merchantOnboardingFieldKinds = ['brief_claim', 'declared_channel', 'experience_preference'];
export const merchantOnboardingFieldKindSchema = z.enum(merchantOnboardingFieldKinds);
export const merchantOnboardingFieldUses = [
    'weave_gate',
    'planner_context',
    'experience_personalization',
    'merchant_display',
];
export const merchantOnboardingFieldUseSchema = z.enum(merchantOnboardingFieldUses);
export const merchantOnboardingSubjectKinds = ['organization', 'connection'];
export const merchantOnboardingSubjectKindSchema = z.enum(merchantOnboardingSubjectKinds);
export const merchantOnboardingAnswerOrigins = [
    'merchant_entered',
    'merchant_confirmed_provider_value',
    'merchant_edited_provider_value',
];
export const merchantOnboardingAnswerOriginSchema = z.enum(merchantOnboardingAnswerOrigins);
export const merchantLearningGoalIds = [
    'purchase_intent_at_price',
    'packaging_presentation',
    'messaging_claims',
    'brand_trust',
    'promotions_offers',
];
export const merchantLearningGoalIdSchema = z.enum(merchantLearningGoalIds);
export const merchantCurrentFocusIds = [
    'launching_new',
    'underperforming_product',
    'deciding_pricing',
    'new_market_audience',
    'responding_to_competitors',
    'exploring',
];
export const merchantCurrentFocusIdSchema = z.enum(merchantCurrentFocusIds);
export const merchantSocialPlatformIds = ['instagram', 'tiktok'];
export const merchantSocialPlatformIdSchema = z.enum(merchantSocialPlatformIds);
export const merchantOnboardingFieldKeys = [
    'store_display_name',
    'business_description',
    'business_category',
    'primary_goal',
    'social_profiles',
    'storefront_domains',
    'learning_goals',
    'current_focus',
];
export const merchantOnboardingFieldKeySchema = z.enum(merchantOnboardingFieldKeys);
function uniqueArray(itemSchema, maximum) {
    return z
        .array(itemSchema)
        .max(maximum)
        .superRefine((items, context) => {
        const serialized = items.map((item) => JSON.stringify(item));
        if (new Set(serialized).size !== serialized.length) {
            context.addIssue({ code: 'custom', message: 'Duplicate values are not allowed' });
        }
    });
}
const socialHandleSchema = z
    .string()
    .trim()
    .min(2)
    .max(100)
    .regex(/^@?[A-Za-z0-9._-]+$/, 'Social handle must contain only handle characters')
    .transform((handle) => (handle.startsWith('@') ? handle : `@${handle}`));
const socialProfileSchema = z.object({
    platform: merchantSocialPlatformIdSchema,
    handle: socialHandleSchema,
});
const storefrontHostSchema = z
    .string()
    .trim()
    .min(1)
    .max(253)
    .transform((input, context) => {
    const urlInput = input.includes('://') ? input : `https://${input}`;
    if (!URL.canParse(urlInput)) {
        context.addIssue({ code: 'custom', message: 'Storefront domain must be a valid host' });
        return z.NEVER;
    }
    const url = new URL(urlInput);
    if (!['http:', 'https:'].includes(url.protocol) ||
        url.username ||
        url.password ||
        url.port ||
        (url.pathname !== '' && url.pathname !== '/') ||
        url.search ||
        url.hash) {
        context.addIssue({ code: 'custom', message: 'Storefront domain must contain only a host' });
        return z.NEVER;
    }
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    if (!host.includes('.')) {
        context.addIssue({ code: 'custom', message: 'Storefront domain must be a valid host' });
        return z.NEVER;
    }
    return host;
});
export const MAX_STOREFRONT_DOMAINS = 4;
export const merchantOnboardingAnswerSchemas = {
    store_display_name: z.string().trim().min(1).max(200),
    business_description: z.string().trim().min(10).max(2000),
    business_category: z.string().trim().min(2).max(200),
    primary_goal: merchantStoreGoalIdSchema,
    social_profiles: uniqueArray(socialProfileSchema, 10),
    storefront_domains: uniqueArray(storefrontHostSchema, MAX_STOREFRONT_DOMAINS),
    learning_goals: uniqueArray(merchantLearningGoalIdSchema, merchantLearningGoalIds.length),
    current_focus: merchantCurrentFocusIdSchema,
};
const learningGoalLabels = {
    purchase_intent_at_price: 'Will people buy this, at this price',
    packaging_presentation: 'Does the packaging or presentation land',
    messaging_claims: 'Does the messaging or claims land',
    brand_trust: 'Do people trust the brand',
    promotions_offers: 'Do promotions or offers move people',
};
const currentFocusLabels = {
    launching_new: 'Launching something new',
    underperforming_product: 'A product or listing underperforming',
    deciding_pricing: 'Deciding on pricing',
    new_market_audience: 'Entering a new market or audience',
    responding_to_competitors: 'Responding to competitors',
    exploring: 'Just exploring',
};
export const merchantOnboardingRegistry = {
    version: MERCHANT_ONBOARDING_QUESTIONNAIRE_VERSION,
    fields: {
        store_display_name: {
            key: 'store_display_name',
            kind: 'brief_claim',
            subjectKind: 'connection',
            uses: ['merchant_display', 'planner_context'],
            question: 'Store name',
            options: null,
        },
        business_description: {
            key: 'business_description',
            kind: 'brief_claim',
            subjectKind: 'connection',
            uses: ['weave_gate', 'planner_context'],
            question: merchantBriefRegistry.fields.business_description.question,
            options: null,
        },
        business_category: {
            key: 'business_category',
            kind: 'brief_claim',
            subjectKind: 'connection',
            uses: ['planner_context'],
            question: 'Product category',
            options: null,
        },
        primary_goal: {
            key: 'primary_goal',
            kind: 'brief_claim',
            subjectKind: 'connection',
            uses: ['weave_gate', 'planner_context'],
            question: merchantBriefRegistry.fields.primary_goal.question,
            options: merchantStoreGoalIds.map((id) => ({ id, label: merchantStoreGoalLabels[id] })),
        },
        social_profiles: {
            key: 'social_profiles',
            kind: 'declared_channel',
            subjectKind: 'connection',
            uses: ['merchant_display'],
            question: 'Social handles',
            options: null,
        },
        storefront_domains: {
            key: 'storefront_domains',
            kind: 'declared_channel',
            subjectKind: 'connection',
            uses: ['merchant_display'],
            question: 'Anywhere else online?',
            options: null,
        },
        learning_goals: {
            key: 'learning_goals',
            kind: 'experience_preference',
            subjectKind: 'organization',
            uses: ['experience_personalization'],
            question: 'What are you hoping to learn, generally?',
            options: merchantLearningGoalIds.map((id) => ({ id, label: learningGoalLabels[id] })),
        },
        current_focus: {
            key: 'current_focus',
            kind: 'experience_preference',
            subjectKind: 'connection',
            uses: ['experience_personalization'],
            question: 'Why now?',
            options: merchantCurrentFocusIds.map((id) => ({ id, label: currentFocusLabels[id] })),
        },
    },
};
export const merchantQuestionnaireRegistries = {
    1: merchantBriefRegistry,
    2: merchantOnboardingRegistry,
};
export const merchantOnboardingCarryForwardRules = [
    { fromVersion: 1, toVersion: 2, fieldKey: 'business_description' },
    { fromVersion: 1, toVersion: 2, fieldKey: 'primary_goal' },
];
/** Validates one answer and its provenance without coercing its JSON shape. */
export function parseMerchantOnboardingAnswerInput(input) {
    const fieldKey = merchantOnboardingFieldKeySchema.parse(input.fieldKey);
    const answer = merchantOnboardingAnswerSchemas[fieldKey].parse(input.answer);
    const origin = merchantOnboardingAnswerOriginSchema.parse(input.origin);
    const hasSourceValue = input.sourceValue !== undefined && input.sourceValue !== null;
    if (origin === 'merchant_entered' && hasSourceValue) {
        throw new Error('Merchant-entered answers must not include a provider source value');
    }
    if (origin !== 'merchant_entered' && !hasSourceValue) {
        throw new Error('Provider-prefilled answers require a source value');
    }
    const sourceValue = hasSourceValue ? z.string().trim().min(1).max(500).parse(input.sourceValue) : null;
    return { fieldKey, answer, origin, sourceValue };
}
/** Stable identity for one subject's field answer at one questionnaire version. */
export function merchantOnboardingAttributeId(input) {
    return `${input.orgId}:${input.subjectKind}:${input.subjectId}:${input.fieldKey}:v${input.version}`;
}
//# sourceMappingURL=merchant-onboarding.js.map